/**
 * A theme's texture on the page background (its texture-* tokens): a seamless tile of grey made from a pattern and a fixed
 * seed, so every load looks alike. Patterns of marks repeat their tile; noise patterns are laid out from it by stochastic
 * tiling into one large image, so the tile never shows.
 * Every pattern averages mid-grey, so laid over the page at the theme's strength it adds the pattern, not a tint.
 */
export const TEXTURE_TILE = 256;

export const PATTERN_NAMES = ['grain', 'stipple', 'paper', 'clouds', 'brushed', 'canvas', 'linen', 'halftone', 'scanlines', 'crosshatch', 'diagonal'] as const;
export type TextureKind = (typeof PATTERN_NAMES)[number];

/** A theme's pattern, or grain when it names none there is. */
export const textureOf = (kind: string | undefined): TextureKind => PATTERN_NAMES.find(name => name === kind) ?? 'grain';

const N = TEXTURE_TILE;

/** mulberry32: small, fast and seeded. */
function seeded(seed: number) {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Smooth noise on a wrapping lattice `period` cells across the tile: it tiles seamlessly. */
function lattice(period: number, random: () => number) {
  const values = Float32Array.from({ length: period * period }, random);
  const at = (i: number, j: number) => values[(((j % period) + period) % period) * period + (((i % period) + period) % period)];
  return (x: number, y: number) => {
    const u = (x * period) / N;
    const v = (y * period) / N;
    const i = Math.floor(u);
    const j = Math.floor(v);
    const fx = u - i;
    const fy = v - j;
    const sx = fx * fx * (3 - 2 * fx);
    const sy = fy * fy * (3 - 2 * fy);
    const a = at(i, j);
    const b = at(i + 1, j);
    const c = at(i, j + 1);
    const d = at(i + 1, j + 1);
    return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
  };
}

/** Octaves of lattice noise, from `period` cells across up, each half as strong: 0 to 1. */
function octaves(period: number, count: number, random: () => number) {
  const layers = Array.from({ length: count }, (_, o) => lattice(period << o, random));
  const total = layers.reduce((sum, _, o) => sum + 0.5 ** o, 0);
  return (x: number, y: number) => layers.reduce((sum, layer, o) => sum + layer(x, y) * 0.5 ** o, 0) / total;
}

const smooth = (edge0: number, edge1: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
};

/** Each pattern: the grey (0 to 1) at every pixel of the tile. */
const PATTERNS: Record<TextureKind, (random: () => number) => (x: number, y: number) => number> = {
  // Film grain: every pixel its own, most near the middle
  grain: random => () => (random() + random()) / 2,
  // Sparse specks, light and dark, on mid-grey
  stipple: random => () => {
    const r = random();
    return r < 0.03 ? 0.15 : r < 0.06 ? 0.85 : 0.5;
  },
  // Soft fibrous mottling with a fine tooth
  paper: random => {
    const fibres = octaves(16, 4, random);
    return (x, y) => 0.5 + (fibres(x, y) - 0.5) * 0.7 + (random() - 0.5) * 0.18;
  },
  clouds: random => {
    const soft = octaves(4, 5, random);
    return (x, y) => 0.5 + (soft(x, y) - 0.5) * 1.1;
  },
  // Long horizontal streaks: each row its own noise along x
  brushed: random => {
    const rows = Array.from({ length: N }, () => [lattice(8, random), lattice(32, random)] as const);
    return (x, y) => {
      const [long, short] = rows[y];
      return 0.5 + (long(x, 0) - 0.5) * 0.45 + (short(x, 0) - 0.5) * 0.25 + (random() - 0.5) * 0.08;
    };
  },
  // Threads 4 px wide, over and under in a checker, each thread a little different
  canvas: random => {
    const warp = Float32Array.from({ length: N / 4 }, () => (random() - 0.5) * 0.12);
    const weft = Float32Array.from({ length: N / 4 }, () => (random() - 0.5) * 0.12);
    return (x, y) => {
      const col = x >> 2;
      const row = y >> 2;
      const over = (col + row) & 1;
      const across = Math.sin((Math.PI * (((over ? x : y) % 4) + 0.5)) / 4);
      return 0.38 + across * 0.24 + (over ? warp[col] : weft[row]) + (random() - 0.5) * 0.06;
    };
  },
  // Fine irregular threads both ways
  linen: random => {
    const cols = Float32Array.from({ length: N }, random);
    const rows = Float32Array.from({ length: N }, random);
    return (x, y) => 0.5 + (cols[x] - 0.5) * 0.28 + (rows[y] - 0.5) * 0.28 + (random() - 0.5) * 0.06;
  },
  // Round dots on an 8 px grid, their edges antialiased
  halftone: () => (x, y) => {
    const dx = (x % 8) + 0.5 - 4;
    const dy = (y % 8) + 0.5 - 4;
    return 0.58 - 0.4 * (1 - smooth(1.8, 2.8, Math.hypot(dx, dy)));
  },
  // A dark line every 4 px
  scanlines: () => (_x, y) => (y % 4 === 0 ? 0.2 : 0.6),
  // Diagonal lines both ways every 8 px
  crosshatch: () => (x, y) => {
    const a = (x + y) % 8;
    const b = (((x - y) % 8) + 8) % 8;
    return a === 0 || b === 0 ? 0.22 : 0.56;
  },
  // Lines one way, rising to the right, 2 px thick every 8 px
  diagonal: () => (x, y) => ((((x + y) % 8) + 8) % 8 < 2 ? 0.25 : 0.58),
};

/** Patterns of repeating marks, shown as their plain tile; the rest are noise, laid out by stochastic tiling. */
const MARKS: ReadonlySet<TextureKind> = new Set(['canvas', 'linen', 'halftone', 'scanlines', 'crosshatch', 'diagonal']);

/** How wide a noise pattern's laid-out image is, in pixels of its tile: it repeats only this far apart. */
const SPREAD = 1536;

/** A square grid of values averaged down from `from` to `to` across, each new cell the area of the old it covers. */
function shrink(values: Float32Array, from: number, to: number) {
  const ratio = from / to;
  const spans = Array.from({ length: to }, (_, d) => {
    const start = d * ratio;
    const end = Math.min(from, start + ratio);
    const weights: [number, number][] = [];
    for (let s = Math.floor(start); s < end; s++) weights.push([s, (Math.min(end, s + 1) - Math.max(start, s)) / ratio]);
    return weights;
  });
  const across = new Float32Array(to * from);
  for (let y = 0; y < from; y++) for (let x = 0; x < to; x++) across[y * to + x] = spans[x].reduce((sum, [s, w]) => sum + values[y * from + s] * w, 0);
  const out = new Float32Array(to * to);
  for (let y = 0; y < to; y++) for (let x = 0; x < to; x++) out[y * to + x] = spans[y].reduce((sum, [s, w]) => sum + across[s * to + x] * w, 0);
  return out;
}

/** The normal distribution's cumulative probability (Abramowitz and Stegun 7.1.26). */
function normalCdf(g: number) {
  const x = Math.abs(g) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * x);
  const erf = 1 - ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);
  return g >= 0 ? (1 + erf) / 2 : (1 - erf) / 2;
}

/** The normal distribution's value at a cumulative probability (Acklam's approximation). */
function normalQuantile(p: number) {
  const a = [-39.69683028665376, 220.9460984245205, -275.9285104469687, 138.357751867269, -30.66479806614716, 2.506628277459239];
  const b = [-54.47609879822406, 161.5858368580409, -155.6989798598866, 66.80131188771972, -13.28068155288572];
  const c = [-0.007784894002430293, -0.3223964580411365, -2.400758277161838, -2.549732539343734, 4.374664141464968, 2.938163982698783];
  const d = [0.007784695709041462, 0.3224671290700398, 2.445134137142996, 3.754408661907416];
  const tail = (q: number) => (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
  if (p < 0.02425) return tail(Math.sqrt(-2 * Math.log(p)));
  if (p > 1 - 0.02425) return -tail(Math.sqrt(-2 * Math.log(1 - p)));
  const q = p - 0.5;
  const r = q * q;
  return ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) / (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
}

/** Where the back-to-grey table covers the normal distribution, either side of its middle. */
const REACH = 5;
const STEPS = 4096;

/**
 * A noise tile `size` pixels across laid out over an image SPREAD wide by stochastic tiling (Heitz and Neyret 2018, Deliot and
 * Heitz 2019): a triangle grid over the image, each corner showing the tile from its own random offset, every pixel a blend of
 * its triangle's three. The blend is made on the tile's values mapped to a normal distribution, keeping its variance, then
 * mapped back, so the image has the tile's own contrast and histogram and no seams or repeat a viewer can find.
 */
function laidOut(tile: Float32Array, size: number) {
  const count = size * size;
  // Each pixel's rank among the tile's, ties broken at random so equal greys spread evenly
  const ties = Float32Array.from({ length: count }, seeded(0x51ed27));
  const order = Array.from({ length: count }, (_, i) => i).sort((p, q) => tile[p] - tile[q] || ties[p] - ties[q]);
  const normal = new Float32Array(count);
  order.forEach((pixel, rank) => (normal[pixel] = normalQuantile((rank + 0.5) / count)));
  const back = Uint8ClampedArray.from({ length: STEPS }, (_, k) => {
    const g = (((k + 0.5) / STEPS) * 2 - 1) * REACH;
    return Math.round(tile[order[Math.min(count - 1, Math.floor(normalCdf(g) * count))]] * 255);
  });

  // The grid: equilateral triangles about 0.29 of the tile across, as Deliot and Heitz, sized to wrap at the image's edges
  const width = SPREAD;
  const cols = Math.max(2, Math.round((width * 2 * Math.sqrt(3)) / size));
  const cell = width / cols;
  const rows = 2 * Math.max(1, Math.round(width / (cell * Math.sqrt(3))));
  const height = Math.round((rows * cell * Math.sqrt(3)) / 2);
  const row = height / rows;
  // Each corner's offset into the tile, less its own place, so a pixel reads the tile relative to the corner
  const random = seeded(0x7a3c91);
  const shiftX = new Int32Array(cols * rows);
  const shiftY = new Int32Array(cols * rows);
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      shiftX[j * cols + i] = Math.floor(random() * size) - Math.round((i + j / 2) * cell);
      shiftY[j * cols + i] = Math.floor(random() * size) - Math.round(j * row);
    }
  }
  const wrap = (value: number, period: number) => ((value % period) + period) % period;
  // The tile's normal value at a pixel as corner (i, j) shows it; a corner past an edge is the one it wraps to, a whole image along
  const corner = (x: number, y: number, i: number, j: number) => {
    const wj = wrap(j, rows);
    const down = (j - wj) / rows;
    const along = i + (down * rows) / 2;
    const wi = wrap(along, cols);
    const across = (along - wi) / cols;
    const at = wj * cols + wi;
    return normal[wrap(y + shiftY[at] - down * height, size) * size + wrap(x + shiftX[at] - across * width, size)];
  };

  const grey = new Uint8ClampedArray(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      // The pixel on the skewed grid, where each triangle is half a unit square
      const v = (y + 0.5) / row;
      const u = (x + 0.5) / cell - v / 2;
      const i = Math.floor(u);
      const j = Math.floor(v);
      const fx = u - i;
      const fy = v - j;
      let sum: number;
      let squares: number;
      if (fx + fy < 1) {
        const w = 1 - fx - fy;
        sum = w * corner(x, y, i, j) + fx * corner(x, y, i + 1, j) + fy * corner(x, y, i, j + 1);
        squares = w * w + fx * fx + fy * fy;
      } else {
        const w = fx + fy - 1;
        sum = w * corner(x, y, i + 1, j + 1) + (1 - fy) * corner(x, y, i + 1, j) + (1 - fx) * corner(x, y, i, j + 1);
        squares = w * w + (1 - fy) * (1 - fy) + (1 - fx) * (1 - fx);
      }
      const g = sum / Math.sqrt(squares);
      grey[y * width + x] = back[Math.min(STEPS - 1, Math.max(0, Math.floor(((g / REACH + 1) / 2) * STEPS)))];
    }
  }
  return { grey, width, height };
}

/** A pattern's tile, `size` pixels across, 0 to 1: drawn at 256 and averaged down for a smaller one, as the browser would show it. */
export function textureTile(kind: TextureKind, size: number) {
  const pattern = PATTERNS[kind](seeded(0x2f6b1d));
  const tile = new Float32Array(N * N);
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) tile[y * N + x] = pattern(x, y);
  return size < N ? shrink(tile, N, size) : tile;
}

/** The size, in pixels, a pattern's tile is laid out at for a tile shown `tile` CSS pixels across: marks always at their own. */
export const layoutSize = (kind: TextureKind, tile: number) => (MARKS.has(kind) ? N : Math.max(8, Math.min(N, Math.round(tile))));

// The last two layouts made (each a few megabytes), newest last, so a change of strength recolours one rather than laying it out again
const layouts = new Map<string, { grey: Uint8ClampedArray; width: number; height: number }>();
const KEEP_LAYOUTS = 2;

/** A pattern's greys as an image, at a layout size: the tile itself for marks, a noise tile laid out for the rest. */
export function textureGreys(kind: TextureKind, size: number) {
  const key = `${kind}:${size}`;
  const kept = layouts.get(key);
  if (kept) {
    layouts.delete(key);
    layouts.set(key, kept);
    return kept;
  }
  const tile = textureTile(kind, size);
  const made = MARKS.has(kind) ? { grey: Uint8ClampedArray.from(tile, value => Math.round(value * 255)), width: N, height: N } : laidOut(tile, size);
  layouts.set(key, made);
  for (const old of layouts.keys()) {
    if (layouts.size <= KEEP_LAYOUTS) break;
    layouts.delete(old);
  }
  return made;
}

/** A texture ready for the page: its image and the size to show it at (a CSS background-size). */
export interface Texture {
  url: string;
  size: string;
}

// The last few images made (an object URL and its size in pixels), newest last; an older one is let go, as a laid-out image is a few megabytes
const made = new Map<string, Promise<{ url: string; width: number; height: number } | null>>();
const KEEP = 4;

/**
 * A pattern at a strength (its opacity, 0 to 1) shown at a tile size in CSS pixels, or null where the page cannot draw one.
 * A noise pattern smaller than the tile is laid out from the tile averaged down to that size, as the browser would show it.
 */
export function textureImage(kind: TextureKind, opacity: number, tile: number): Promise<Texture | null> {
  const size = layoutSize(kind, tile);
  const key = `${kind}:${size}:${opacity}`;
  let image = made.get(key);
  if (image) made.delete(key);
  else {
    image = new Promise(done => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return done(null);
      const { grey, width, height } = textureGreys(kind, size);
      canvas.width = width;
      canvas.height = height;
      const data = context.createImageData(width, height);
      const alpha = Math.round(opacity * 255);
      grey.forEach((value, i) => {
        data.data.fill(value, i * 4, i * 4 + 3);
        data.data[i * 4 + 3] = alpha;
      });
      context.putImageData(data, 0, 0);
      canvas.toBlob(blob => done(blob ? { url: URL.createObjectURL(blob), width, height } : null), 'image/png');
    });
  }
  made.set(key, image);
  for (const [old, oldImage] of made) {
    if (made.size <= KEEP) break;
    made.delete(old);
    void oldImage.then(made => made && URL.revokeObjectURL(made.url));
  }
  const scale = tile / size;
  return image.then(made => made && { url: made.url, size: `${made.width * scale}px ${made.height * scale}px` });
}
